import type { EfGetMemberClassReservationsResponse } from '@form-factory/schema/contracts';
import { DateFormat, formatUtils } from '@form-factory/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { aiCoreApi } from '@/services/aiCoreClient';
import { groupClassesKeys, reservationsKeys } from '@/services/queryClient';

import { useUser } from './useUser';

const EMPTY_RESERVATIONS: EfGetMemberClassReservationsResponse['results'] = [];

/**
 * Hook for managing users current reservations, while also
 * providing utilities to register for new classes or cancel existing ones.
 */
export function useClassReservations({
  dateFrom,
  clubId,
}: {
  dateFrom?: Date;
  clubId?: string;
} = {}) {
  const { user, accessToken } = useUser();
  const queryClient = useQueryClient();
  const from = formatUtils.date(dateFrom ?? new Date(), DateFormat.DateInput);
  const currentClubId = clubId ?? user?.clubId;

  const reservationsQuery = useQuery({
    queryKey: [...reservationsKeys.lists(), currentClubId, from],
    queryFn: async () =>
      aiCoreApi.eFitness.groupClasses.findAllReservations({
        clubId: currentClubId!,
        DateFrom: from,
      }),
    // Only registered users can have reservations
    enabled: !!currentClubId && !!user?.externalId && !!from && !!accessToken,
    staleTime: 5000 * 60, // 5 minutes
  });

  const reservations = reservationsQuery.data?.results ?? EMPTY_RESERVATIONS;
  const reservationsCount = reservations.length;

  // Cancel reservation mutation
  const cancelReservation = useMutation({
    mutationFn: async (reservationId: number) => {
      await aiCoreApi.eFitness.groupClasses.cancelReservation({
        clubId: currentClubId!,
        id: reservationId.toString(),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: reservationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupClassesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupClassesKeys.details() });
    },
  });

  // Create reservation mutation
  const createReservation = useMutation({
    mutationFn: async (classId: number) => {
      await aiCoreApi.eFitness.groupClasses.createReservation({
        clubId: currentClubId!,
        classId,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: reservationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupClassesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupClassesKeys.details() });
    },
  });

  // Find reservation by classId
  const findReservation = useCallback(
    (classId: number | undefined) => {
      if (!classId) {
        return null;
      }

      return reservations.find(reservation => reservation.classId == classId);
    },
    [reservations],
  );

  // Check if user has a reservation for a given classId
  const hasReservation = useCallback(
    (classId: number | undefined) => findReservation(classId) !== null,
    [findReservation],
  );

  return {
    error: reservationsQuery.error,
    isLoading: reservationsQuery.isLoading,
    reservations,
    reservationsCount,
    hasReservation,
    findReservation,
    cancelReservation,
    createReservation,
  };
}
